import React, { useState } from "react";
import {
  Modal,
  Form,
  Row,
  Col,
  Select,
  InputNumber,
  Input,
  Upload,
  Button,
  Space,
  App,
} from "antd";
import type { UploadFile } from "antd/es/upload";
import { useTranslation } from "react-i18next";
import { PlusOutlined } from "@ant-design/icons";
import {
  useGetAllPartnersSelectQuery,
  useAddLedgerTransactionMutation,
  useUploadFileMutation,
  useGetPartnerStoresLazyQuery,
} from "../graphql/generated/hooks";

const { Option } = Select;

// Transaction status enum
enum TransactionStatus {
  PENDING = "PENDING",
  POSTED = "POSTED",
  CANCELLED = "CANCELLED",
}

// Transaction type enum
enum TransactionType {
  ORDER_CAPTURE = "ORDER_CAPTURE",
  PLATFORM_COMMISSION = "PLATFORM_COMMISSION",
  DELIVERY_FEE = "DELIVERY_FEE",
  GATEWAY_FEE = "GATEWAY_FEE",
  PAYOUT = "PAYOUT",
  REFUND = "REFUND",
  ADJUSTMENT = "ADJUSTMENT",
  TIP = "TIP",
  PARTNER_DELIVERY_COVERAGE = "PARTNER_DELIVERY_COVERAGE",
  DISCOUNT_PARTNER_COVERAGE = "DISCOUNT_PARTNER_COVERAGE",
  ADDITIONAL_CUSTOMER_FEE = "ADDITIONAL_CUSTOMER_FEE",
  FAST_DELIVERY_FEE = "FAST_DELIVERY_FEE",
  DISTANCE_FEE = "DISTANCE_FEE",
}

interface AddTransactionModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

function AddTransactionModal({ open, onCancel, onSuccess }: AddTransactionModalProps) {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  
  const { data: partnersData, loading: partnersLoading } =
    useGetAllPartnersSelectQuery();
  const [addTransaction, { loading: addingTransaction }] = useAddLedgerTransactionMutation();
  const [uploadFile, { loading: uploading }] = useUploadFileMutation();
  const [getPartnerStores, { data: storesData, loading: storesLoading }] = useGetPartnerStoresLazyQuery();

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadedMediaData, setUploadedMediaData] = useState<any>(null);
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(null);

  // Get stores from the dedicated query
  const availableStores = storesData?.getPartnerStores || [];

  // Fetch stores when partner is selected
  const handlePartnerChange = (partnerId: number) => {
    setSelectedPartnerId(partnerId);
    form.setFieldsValue({ storeId: undefined });
    
    if (partnerId) {
      getPartnerStores({
        variables: { partnerId },
      });
    }
  };

  // Convert file to base64
  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });
  };

  // Handle file upload
  const handleCustomUpload = async (options: any) => {
    const { file, onSuccess: uploadSuccess, onError, onProgress } = options;
    
    try {
      onProgress({ percent: 0 });
      
      const base64String = await convertFileToBase64(file);
      onProgress({ percent: 50 });
      
      const result = await uploadFile({
        variables: {
          base64File: base64String,
          originalName: file.name,
          folder: 'transactions',
        },
      });
      
      onProgress({ percent: 100 });
      
      if (result.data?.uploadFile) {
        const mediaData = {
          fileName: result.data.uploadFile.fileName,
          originalName: result.data.uploadFile.originalName,
          mimeType: result.data.uploadFile.mimeType,
          size: result.data.uploadFile.size,
          type: 'IMAGE',
          url: result.data.uploadFile.url,
          altText: result.data.uploadFile.altText,
          description: result.data.uploadFile.description,
        };
        
        setUploadedMediaData(mediaData);
        
        uploadSuccess({
          url: result.data.uploadFile.url,
        }, file);
      } else {
        onError(new Error('Upload failed: No data returned'));
      }
    } catch (error) {
      onError(error);
    }
  };

  const handleRemove = () => {
    setUploadedMediaData(null);
    setFileList([]);
  };

  // Handle form submission
  const handleSubmit = async (values: any) => {
    try {
      await addTransaction({
        variables: {
          amountMinor: values.amountMinor.toString(),
          description: values.description,
          orderId: values.orderId,
          status: values.status,
          storeId: values.storeId,
          type: values.type,
          media: uploadedMediaData,
        },
      });
      
      message.success(t("message.success.data_created"));
      onSuccess();
    } catch (error: any) {
      message.error(error.message || t("message.error.data_create_failed"));
    }
  };

  const uploadButton = (
    <button style={{ border: 0, background: "none" }} type="button">
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>{t("button.upload")}</div>
    </button>
  );

  return (
    <Modal
      title={t("title.add_transaction")}
      open={open}
      onCancel={onCancel}
      footer={null}
      width={800}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="partnerId"
              label={t("label.transaction.partner_name")}
              rules={[{ required: true, message: t("message.error.field_required") }]}
            >
              <Select
                placeholder={t("label.partner.select")}
                loading={partnersLoading}
                showSearch
                onChange={handlePartnerChange}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={partnersData?.getAllPartners?.map((partner) => ({
                  label: `${partner.name} (${partner.nameAr})`,
                  value: partner.id,
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="storeId"
              label={t("label.transaction.store_name")}
              rules={[{ required: true, message: t("message.error.field_required") }]}
            >
              <Select
                placeholder={t("label.store.select")}
                disabled={!selectedPartnerId}
                loading={storesLoading}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={availableStores.map((store: any) => {
                  const currentLang = i18n.language;
                  const storeName = currentLang === 'ar' ? (store.nameAr || store.name) : store.name;
                  return {
                    label: storeName,
                    value: store.id,
                  };
                })}
                notFoundContent={storesLoading ? t("label.loading") : t("label.no_stores")}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="type"
              label={t("label.transaction.type")}
              rules={[{ required: true, message: t("message.error.field_required") }]}
            >
              <Select placeholder={t("label.transaction.type")}>
                {Object.values(TransactionType).map((type) => (
                  <Option key={type} value={type}>
                    {t(`transaction.type.${type.toLowerCase()}`)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="status"
              label={t("label.transaction.status")}
              rules={[{ required: true, message: t("message.error.field_required") }]}
            >
              <Select placeholder={t("label.transaction.status")}>
                {Object.values(TransactionStatus).map((status) => (
                  <Option key={status} value={status}>
                    {t(`transaction.status.${status.toLowerCase()}`)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="amountMinor"
              label={t("label.transaction.amount")}
              rules={[{ required: true, message: t("message.error.field_required") }]}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder={t("label.transaction.amount")}
                addonAfter={t("label.currency")}
                step={0.01}
                precision={2}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="orderId"
              label={t("label.transaction.order_id")}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder={t("label.transaction.order_id")}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              name="description"
              label={t("label.transaction.description")}
            >
              <Input.TextArea
                rows={3}
                placeholder={t("label.transaction.description")}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              label={t("label.media")}
            >
              <Upload
                listType="picture-card"
                fileList={fileList}
                onChange={({ fileList }) => setFileList(fileList)}
                onRemove={handleRemove}
                customRequest={handleCustomUpload}
                maxCount={1}
                accept="image/*"
              >
                {fileList.length >= 1 ? null : uploadButton}
              </Upload>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item style={{ marginBottom: 0 }}>
          <Space>
            <Button 
              type="primary" 
              htmlType="submit"
              loading={addingTransaction || uploading}
            >
              {t("button.add")}
            </Button>
            <Button onClick={onCancel}>
              {t("button.cancel")}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default AddTransactionModal;

